import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import SquareButton from '@/components/SquareButton';
import { BRAND, NAV_LINKS } from '@/lib/constants';

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-[var(--banner-offset)] z-40 w-full bg-[hsl(var(--c-cream))] border-b-2 border-[hsl(var(--c-ink))]"
      style={{ height: 'var(--nav-offset)' }}
    >
      <div className="mx-auto h-full max-w-[1400px] flex items-center px-5 sm:px-8 lg:px-16">
        {/* Logo */}
        <Link to="/" className="logo-frame shrink-0" data-testid="nav-logo-link" aria-label="Florida SunKissed Mangoes — Home">
          <img
            src={BRAND.logos.primary}
            alt="Florida SunKissed Mangoes logo"
            width="180"
            height="60"
            decoding="async"
            loading="eager"
            className="h-12 sm:h-14 w-auto"
          />
        </Link>

        <div className="flex-1" />

        {/* Desktop spaced nav */}
        <nav className="hidden xl:flex items-center gap-9 mr-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `relative text-[12.5px] font-bold uppercase tracking-[0.18em] py-2 btn-transition ${
                  isActive ? 'text-[hsl(var(--c-mango-red))]' : 'text-[hsl(var(--c-ink))] hover:text-[hsl(var(--c-mango-orange))]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[hsl(var(--c-mango-red))]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Compact lg nav */}
        <nav className="hidden lg:flex xl:hidden items-center gap-5 mr-5" aria-label="Primary (compact)">
          {NAV_LINKS.slice(0, 5).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`nav-link-md-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `text-[11.5px] font-bold uppercase tracking-[0.16em] py-2 btn-transition ${
                  isActive ? 'text-[hsl(var(--c-mango-red))]' : 'text-[hsl(var(--c-ink))] hover:text-[hsl(var(--c-mango-orange))]'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-0 shrink-0">
          <SquareButton to="/wholesale" variant="ghost" size="md" testId="nav-wholesale-cta">Wholesale</SquareButton>
          <SquareButton to="/shop" variant="orange" size="md" testId="nav-shop-cta" className="-ml-[2px]">Shop Boxes</SquareButton>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-nav-open-button"
                className="h-11 w-11 border-2 border-[hsl(var(--c-ink))] bg-white inline-flex items-center justify-center btn-transition hover:bg-[hsl(var(--c-cream-deep))]"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[hsl(var(--c-cream))] border-l-2 border-[hsl(var(--c-ink))] w-[88%] sm:w-[420px] p-0 flex flex-col"
            >
              <div className="flex items-center justify-between p-5 border-b-2 border-[hsl(var(--c-ink))] bg-[hsl(var(--c-mango-yellow))]">
                <img src={BRAND.logos.primary} alt="" width="160" height="56" className="h-12 w-auto" />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="h-10 w-10 border-2 border-[hsl(var(--c-ink))] bg-white inline-flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col overflow-y-auto flex-1" aria-label="Mobile">
                {NAV_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === '/'}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                      `px-6 py-5 border-b border-[hsl(var(--c-ink))]/15 text-base font-bold uppercase tracking-[0.16em] btn-transition ${
                        isActive
                          ? 'text-[hsl(var(--c-mango-red))] bg-white'
                          : 'text-[hsl(var(--c-ink))] hover:bg-white'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-5 grid gap-0 border-t-2 border-[hsl(var(--c-ink))]">
                <SquareButton to="/shop" variant="orange" size="lg" fullWidth onClick={() => setOpen(false)}>Shop Mango Boxes</SquareButton>
                <SquareButton to="/wholesale" variant="teal" size="lg" fullWidth onClick={() => setOpen(false)} className="-mt-[2px]">Wholesale Inquiry</SquareButton>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
